
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Award, Calendar, BarChart } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { format, subDays, subWeeks, subMonths, isAfter } from 'date-fns';

// Mock data for leaderboard users
const MOCK_USERS = [
  { id: 'user-1', name: 'John Smith', avatar: '/placeholder.svg', gifts: 24, streak: 5 },
  { id: 'user-2', name: 'Sarah Johnson', avatar: '/placeholder.svg', gifts: 19, streak: 3 },
  { id: 'user-3', name: 'Mike Wilson', avatar: '/placeholder.svg', gifts: 16, streak: 7 },
  { id: 'user-4', name: 'Emma Davis', avatar: '/placeholder.svg', gifts: 14, streak: 2 },
  { id: 'user-5', name: 'Alex Garcia', avatar: '/placeholder.svg', gifts: 12, streak: 4 },
  { id: 'user-6', name: 'Lisa Brown', avatar: '/placeholder.svg', gifts: 10, streak: 1 },
  { id: 'user-7', name: 'Robert Miller', avatar: '/placeholder.svg', gifts: 8, streak: 3 },
  { id: 'user-8', name: 'Jennifer Lee', avatar: '/placeholder.svg', gifts: 6, streak: 2 },
];

// Mock data for recent transactions to calculate leaderboard
const MOCK_TRANSACTIONS = [
  { id: 'tx-1', userId: 'user-1', timestamp: new Date(2025, 3, 7, 9, 30) },
  { id: 'tx-2', userId: 'user-1', timestamp: new Date(2025, 3, 6, 14, 15) },
  { id: 'tx-3', userId: 'user-2', timestamp: new Date(2025, 3, 6, 11, 45) },
  { id: 'tx-4', userId: 'user-3', timestamp: new Date(2025, 3, 5, 16, 20) },
  { id: 'tx-5', userId: 'user-1', timestamp: new Date(2025, 3, 5, 10, 10) },
  { id: 'tx-6', userId: 'user-2', timestamp: new Date(2025, 3, 4, 9, 0) },
  { id: 'tx-7', userId: 'user-4', timestamp: new Date(2025, 3, 4, 14, 35) },
  { id: 'tx-8', userId: 'user-3', timestamp: new Date(2025, 3, 3, 11, 25) },
  { id: 'tx-9', userId: 'user-1', timestamp: new Date(2025, 3, 3, 8, 15) },
  { id: 'tx-10', userId: 'user-5', timestamp: new Date(2025, 3, 2, 13, 40) },
  { id: 'tx-11', userId: 'user-2', timestamp: new Date(2025, 3, 2, 10, 5) },
  { id: 'tx-12', userId: 'user-3', timestamp: new Date(2025, 3, 1, 15, 50) },
  { id: 'tx-13', userId: 'user-4', timestamp: new Date(2025, 3, 1, 9, 30) },
  { id: 'tx-14', userId: 'user-1', timestamp: new Date(2025, 2, 28, 14, 20) },
  { id: 'tx-15', userId: 'user-5', timestamp: new Date(2025, 2, 28, 16, 15) },
  { id: 'tx-16', userId: 'user-6', timestamp: new Date(2025, 2, 27, 11, 45) },
  { id: 'tx-17', userId: 'user-7', timestamp: new Date(2025, 2, 27, 9, 5) },
  { id: 'tx-18', userId: 'user-8', timestamp: new Date(2025, 2, 26, 15, 30) },
  { id: 'tx-19', userId: 'user-2', timestamp: new Date(2025, 2, 26, 14, 0) },
  { id: 'tx-20', userId: 'user-3', timestamp: new Date(2025, 2, 25, 10, 20) },
  // Additional transactions to fill out user history
  ...Array.from({ length: 80 }, (_, i) => ({
    id: `tx-extra-${i + 1}`,
    userId: MOCK_USERS[Math.floor(Math.random() * MOCK_USERS.length)].id,
    timestamp: new Date(2025, 2, Math.floor(Math.random() * 25) + 1),
  })),
];

const RecentActivity: React.FC = () => {
  const [timeFrame, setTimeFrame] = useState<'day' | 'week' | 'month'>('week');
  
  const filterTransactionsByTime = () => {
    const now = new Date();
    let cutoffDate;
    
    switch (timeFrame) {
      case 'day':
        cutoffDate = subDays(now, 1);
        break;
      case 'week':
        cutoffDate = subWeeks(now, 1);
        break;
      case 'month':
        cutoffDate = subMonths(now, 1);
        break;
    }
    
    return MOCK_TRANSACTIONS.filter(tx => 
      isAfter(tx.timestamp, cutoffDate)
    );
  };
  
  const calculateLeaderboard = () => {
    const filteredTransactions = filterTransactionsByTime();
    
    // Count transactions per user
    const userCounts = filteredTransactions.reduce((counts, tx) => {
      counts[tx.userId] = (counts[tx.userId] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);
    
    // Create leaderboard array with user data
    const leaderboard = Object.entries(userCounts).map(([userId, count]) => {
      const user = MOCK_USERS.find(u => u.id === userId);
      return {
        id: userId,
        name: user?.name || 'Unknown User',
        avatar: user?.avatar || '/placeholder.svg',
        transactionCount: count,
        streak: user?.streak || 0
      };
    });
    
    // Sort by transaction count (descending)
    return leaderboard.sort((a, b) => b.transactionCount - a.transactionCount);
  };
  
  const leaderboard = calculateLeaderboard();
  
  const getTimeFrameTitle = () => {
    switch (timeFrame) {
      case 'day':
        return 'Daily Givlings Leaderboard';
      case 'week':
        return 'Weekly Givlings Leaderboard';
      case 'month':
        return 'Monthly Givlings Leaderboard';
    }
  };
  
  const getTimeIcon = () => {
    switch (timeFrame) {
      case 'day':
        return <Calendar className="h-5 w-5 text-blue-500" />;
      case 'week':
        return <Award className="h-5 w-5 text-purple-500" />;
      case 'month':
        return <BarChart className="h-5 w-5 text-green-500" />;
    }
  };
  
  const getLeaderBadge = (position: number) => {
    if (position === 0) {
      return <Badge className="bg-yellow-400 hover:bg-yellow-500">🏆 #1</Badge>;
    } else if (position === 1) {
      return <Badge className="bg-gray-400 hover:bg-gray-500">🥈 #2</Badge>;
    } else if (position === 2) {
      return <Badge className="bg-amber-600 hover:bg-amber-700">🥉 #3</Badge>;
    }
    return <Badge variant="outline">{`#${position + 1}`}</Badge>;
  };

  return (
    <div className="mt-8">
      <h3 className="font-medium mb-4 flex items-center gap-2">
        <Award className="h-5 w-5 text-yellow-500" />
        Leaderboard
      </h3>
      <Card>
        <div className="p-4">
          <Tabs defaultValue="week" value={timeFrame} onValueChange={(value) => setTimeFrame(value as 'day' | 'week' | 'month')}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                {getTimeIcon()}
                <h4 className="font-medium">{getTimeFrameTitle()}</h4>
              </div>
              <TabsList>
                <TabsTrigger value="day">Today</TabsTrigger>
                <TabsTrigger value="week">This Week</TabsTrigger>
                <TabsTrigger value="month">This Month</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="day" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Rank</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Gifts Given</TableHead>
                    <TableHead className="text-right">Streak</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.slice(0, 5).map((user, index) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {getLeaderBadge(index)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span>{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{user.transactionCount}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span>{user.streak}</span>
                          <span className="text-orange-500">🔥</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
            
            <TabsContent value="week" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Rank</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Gifts Given</TableHead>
                    <TableHead className="text-right">Streak</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.slice(0, 5).map((user, index) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {getLeaderBadge(index)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span>{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{user.transactionCount}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span>{user.streak}</span>
                          <span className="text-orange-500">🔥</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
            
            <TabsContent value="month" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Rank</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Gifts Given</TableHead>
                    <TableHead className="text-right">Streak</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.slice(0, 5).map((user, index) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {getLeaderBadge(index)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span>{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{user.transactionCount}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span>{user.streak}</span>
                          <span className="text-orange-500">🔥</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </div>
      </Card>
    </div>
  );
};

export default RecentActivity;
